import { useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { parseRoute, paths, type RouteInfo } from "./paths";

export type Nav = {
  readonly dashboard: () => void;
  readonly newAgent: (repoId?: string) => void;
  readonly repo: (repoId: string) => void;
  readonly settings: () => void;
  readonly task: (
    repoId: string,
    taskLabel: string,
    tab?: string,
    sub?: string,
  ) => void;
};

/**
 * Programmatic navigation, for the cases where the move follows something the
 * user did rather than something they clicked: a keyboard shortcut, or the
 * redirect after launching an agent, archiving a task or deleting a repo.
 *
 * A control whose whole job is "go here" is **not** one of those cases — it
 * renders a `<Link>` / `<ButtonLink>` to `paths.*` so it can be opened in a new
 * tab (frontend.md §Hard rules). Only add a member here when a real
 * post-action redirect needs it.
 */
export const useNav = (): Nav => {
  const navigate = useNavigate();
  return useMemo<Nav>(
    () => ({
      dashboard: () => void navigate(paths.dashboard()),
      newAgent: (repoId) => void navigate(paths.newAgent(repoId)),
      repo: (repoId) => void navigate(paths.repo(repoId)),
      settings: () => void navigate(paths.settings()),
      task: (repoId, taskLabel, tab, sub) =>
        void navigate(paths.task(repoId, taskLabel, tab, sub)),
    }),
    [navigate],
  );
};

export const useRouteInfo = (): RouteInfo => {
  const { pathname } = useLocation();
  return useMemo(() => parseRoute(pathname), [pathname]);
};

/**
 * The repository the current route is about, or `undefined` outside one.
 *
 * The single source for anything "scoped to the current repo when one is
 * active, otherwise global" (command-palette-and-search.md §Actions): the `N`
 * shortcut and the palette's own **Start new agent** row both read it, so the
 * hint and the handler cannot land in different places.
 */
export const useActiveRepoId = (): string | undefined => useRouteInfo().repoId;
